import styled from '@emotion/styled';
import { Box } from '@mui/material';
import Button from '@mui/material/Button';
import { PropsWithChildren } from 'react';
import { useTranslation } from 'react-i18next';
import { useModal } from '@/context/Modal.context';
import {
  useQualityControlTool,
  MODAL_TYPES,
} from '@/context/Qualitycontrol.context';
import { QC_DECISION_STATE } from '@/helpers/states';
import { ErrorField, ErrorType } from '@/types/QcInfo';
import { QC_DECISION_STATE_TYPE } from '@/types/QcStatus';

const CustomizedButton = styled(Button)`
  box-shadow: none;
  margin: 1rem;
  padding: 0.5rem 2rem;
`;

const Wrapper = ({ children }: PropsWithChildren) => {
  return (
    <Box
      component="footer"
      sx={(theme) => ({
        display: 'flex',
        width: '100%',
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: theme.palette.background.paper,
      })}
    >
      {children}
    </Box>
  );
};

const RejectButton = ({
  disabled,
  onClick,
}: {
  disabled: boolean;
  onClick: () => void;
}) => {
  const { t } = useTranslation();

  return (
    <CustomizedButton
      data-testid="qc_ko_button"
      variant="contained"
      size="large"
      disabled={disabled}
      sx={{ backgroundColor: '#F44336' }}
      onClick={onClick}
    >
      {t('QC_V2.MAIN.QC_KO')}
    </CustomizedButton>
  );
};

const OnHoldButton = ({ onClick }: { onClick: () => void }) => {
  const { t } = useTranslation();

  return (
    <CustomizedButton
      data-testid="on_hold_button"
      variant="outlined"
      size="large"
      sx={{
        color: '#2196F3',
      }}
      onClick={onClick}
    >
      {t('QC_V2.MAIN.QC_ON_HOLD')}
    </CustomizedButton>
  );
};

interface QcSubmitButtonProps {
  isWeightHallmarkRequired: boolean;
  isWatchFunctionedRequired: boolean;
  setErrors: (v: ErrorType[]) => void;
}

export default function QcSubmitButtons({
  setErrors,
  isWeightHallmarkRequired,
  isWatchFunctionedRequired,
}: QcSubmitButtonProps) {
  const { t } = useTranslation();

  const { setModalInfo } = useModal();

  const {
    qcData,
    defects,
    weight,
    hallmarks,
    additionalInfo,
    originCountry,
    isUSShipping,
  } = useQualityControlTool();

  const isNotForSale = !!qcData?.product?.notForSaleReasons.length;

  const isProhibited = defects.find((defect) => defect.isProhibited);

  const isForceKo = defects.some((defect) => !!defect.defectType?.forceKo);

  const isRejectDisabled = !isProhibited && !defects.length;

  const validate = (callback: Function) => {
    const errors: ErrorType[] = [];

    if (isWeightHallmarkRequired) {
      if (!weight) {
        errors.push({
          field: ErrorField.WEIGHT,
          message: t('QC_V2.DEFECT.REQUIRED'),
        });
      }
      if (!hallmarks) {
        errors.push({
          field: ErrorField.HALLMARKS,
          message: t('QC_V2.DEFECT.REQUIRED'),
        });
      }
    }

    if (isWatchFunctionedRequired) {
      if (additionalInfo.isWorkingWatch == null) {
        errors.push({
          field: ErrorField.WATCH,
          message: t('QC_V2.DEFECT.REQUIRED'),
        });
      }
    }

    if (isUSShipping && !originCountry.isConfirmed) {
      errors.push({
        field: ErrorField.ORIGIN_COUNTRY,
        message: t('QC_V2.MAIN.ORIGIN_COUNTRY_CONFIRM_MSG'),
      });
    }

    if (errors.length) {
      setErrors(errors);
      return;
    }

    callback();
  };

  const openSubmitModal = ({ result }: { result: QC_DECISION_STATE_TYPE }) => {
    setModalInfo({
      modalType: MODAL_TYPES.QC_SUBMIT,
      modalInfo: {
        qcResult: result,
      },
    });
  };

  const onReject = () =>
    validate(() =>
      openSubmitModal({
        result: QC_DECISION_STATE.REJECT,
      })
    );

  const onPutOnHold = () =>
    openSubmitModal({
      result: QC_DECISION_STATE.HOLD,
    });

  // For Force-KO only show Reject button
  if (isForceKo) {
    return (
      <Wrapper>
        <RejectButton disabled={isRejectDisabled} onClick={onReject} />
        <OnHoldButton onClick={onPutOnHold} />
      </Wrapper>
    );
  }

  return (
    <Wrapper>
      {!isNotForSale && !isProhibited && (
        <CustomizedButton
          data-testid="qc_ok_button"
          variant="contained"
          size="large"
          disabled={isNotForSale || isProhibited || !!defects.length}
          sx={{ mr: 2, backgroundColor: '#4CAF50' }}
          onClick={() => {
            validate(() =>
              openSubmitModal({
                result: QC_DECISION_STATE.ACCEPT,
              })
            );
          }}
        >
          {t('QC_V2.MAIN.QC_OK')}
        </CustomizedButton>
      )}
      {!isNotForSale && !isProhibited && (
        <CustomizedButton
          data-testid="qc_nego_button"
          variant="contained"
          size="large"
          disabled={isNotForSale || isProhibited || !defects.length}
          sx={{ backgroundColor: '#FFA000' }}
          onClick={() => {
            validate(() =>
              openSubmitModal({
                result: QC_DECISION_STATE.NEGOTIATE,
              })
            );
          }}
        >
          {t('QC_V2.MAIN.QC_NEGO')}
        </CustomizedButton>
      )}

      <RejectButton disabled={isRejectDisabled} onClick={onReject} />
      <OnHoldButton onClick={onPutOnHold} />
    </Wrapper>
  );
}
